import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconLoader2, IconDownload } from '@tabler/icons-react';
import { Button } from '@core-ui/Components/ui/button';
import { Badge } from '@core-ui/Components/ui/badge';
import { Skeleton } from '@core-ui/Components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@core-ui/Components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@core-ui/Components/ui/table';
import { PaginationControls } from '@ui/shared/paginationControls';
import ReportDownloadModal, { type ReportType } from './ReportDownloadModal';
import { type DashboardSession, type ContactListType, SessionStatus } from '../types/dashboard.types';
import { getCurrentUser } from '@features/auth';
import { Check, Copy } from 'lucide-react';

interface SessionsTableProps {
  sessions: DashboardSession[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  onSessionClick: (sessionId: string) => void;
  onDownloadReport: (sessionId: string, type: ReportType, listType: ContactListType, organizationId?: string) => Promise<void>;
  downloadingSessionId: string | null;
}

const TYPE_COLORS: Record<ContactListType, string> = {
  HUBSPOT: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
  CUSTOM_HUBSPOT: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
  OUTREACH: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/30',
  SALESFORCE: 'bg-sky-500/10 text-sky-500 border-sky-500/30',
  CSV: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  FOURMATIV: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
};

const TYPE_LABELS: Record<ContactListType, string> = {
  HUBSPOT: 'HubSpot',
  CUSTOM_HUBSPOT: 'HubSpot',
  OUTREACH: 'Outreach',
  SALESFORCE: 'Salesforce',
  CSV: 'CSV',
  FOURMATIV: 'Fourmativ',
};

// Badge colours follow the StreamingPoint beside them: a live session pulses
// emerald, a paused one sits still in amber, and a finished one is neutral.
const STATUS_COLORS: Record<SessionStatus, string> = {
  [SessionStatus.FINISHED]: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
  [SessionStatus.STARTED]: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  [SessionStatus.PAUSED]: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
};

const STATUS_LABELS: Record<SessionStatus, string> = {
  [SessionStatus.FINISHED]: 'dashboard.sessionsTable.statusFinished',
  [SessionStatus.STARTED]: 'dashboard.sessionsTable.statusStarted',
  [SessionStatus.PAUSED]: 'dashboard.sessionsTable.statusPaused',
};

// Handle MongoDB Extended JSON date format or regular ISO string
function formatDate (
  dateValue: string | { $date: string } | null | undefined,
  lang: string
): string {
  if (dateValue == null) {
    return '-';
  }

  const localeLower = (lang ?? '').toLowerCase();
  const locale =
      localeLower.startsWith('es') ? 'es-ES' :
        localeLower.startsWith('en') ? 'en-US' :
          'en-US';

  let dateString: string;
  if (typeof dateValue === 'object' && dateValue.$date !== '') {
    dateString = dateValue.$date;
  } else if (typeof dateValue === 'string') {
    dateString = dateValue;
  } else {
    return '-';
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return '-';
  }

  const dateStr = date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return `${dateStr}, ${timeStr}`;
}

function getInitials (name: string): string {
  if (name === '') {
    return '??';
  }
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function StreamingPoint ({ paused = false }: { paused?: boolean }): React.JSX.Element {
  const color = paused ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="relative flex items-center justify-center w-3 h-3" data-qa-id={paused ? 'session-paused-point' : 'session-streaming-point'}>
      {!paused && <div className={`absolute h-full w-full animate-ping rounded-full ${color} opacity-75`}/>}
      <span className={`relative w-2 h-2 rounded-full ${color}`}></span>
    </div>
  );
}

function TableSkeleton ({
  rowCount = 5,
  showSessionId = false,
}: { rowCount?: number; showSessionId?: boolean }): React.JSX.Element {
  const skeletonClass = 'bg-muted';
  return (
    <>
      {Array.from({ length: rowCount }).map((_, i) => (
        <TableRow key={i} className="border-b border-border/60">
          <TableCell>
            <div className="flex items-center gap-3">
              <Skeleton className={`h-8 w-8 rounded-full ${skeletonClass}`} />
              <Skeleton className={`h-4 w-24 ${skeletonClass}`} />
            </div>
          </TableCell>
          {/* Mirrors the real row, or the skeleton is a column short and the
              whole table shifts sideways the moment the data lands. */}
          {showSessionId && (
            <TableCell><Skeleton className={`h-4 w-40 ${skeletonClass}`} /></TableCell>
          )}
          <TableCell><Skeleton className={`h-4 w-48 ${skeletonClass}`} /></TableCell>
          <TableCell><Skeleton className={`h-4 w-28 ${skeletonClass}`} /></TableCell>
          <TableCell><Skeleton className={`h-6 w-20 rounded-full ${skeletonClass}`} /></TableCell>
          <TableCell className="text-right"><Skeleton className={`h-4 w-10 ml-auto ${skeletonClass}`} /></TableCell>
          <TableCell className="text-right"><Skeleton className={`h-4 w-10 ml-auto ${skeletonClass}`} /></TableCell>
          <TableCell className="text-right"><Skeleton className={`h-4 w-12 ml-auto ${skeletonClass}`} /></TableCell>
          <TableCell className="text-right"><Skeleton className={`h-4 w-8 ml-auto ${skeletonClass}`} /></TableCell>
          <TableCell><Skeleton className={`h-4 w-32 ${skeletonClass}`} /></TableCell>
          <TableCell><Skeleton className={`h-6 w-20 rounded-full ${skeletonClass}`} /></TableCell>
          <TableCell className="text-center">
            <Skeleton className={`h-8 w-8 mx-auto rounded ${skeletonClass}`} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export default function SessionsTable ({
  sessions,
  loading,
  currentPage,
  totalPages,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSessionClick,
  onDownloadReport,
  downloadingSessionId,
}: SessionsTableProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const currentUser = getCurrentUser();
  const isTendrilOrgUser = currentUser?.isTendrilUser;
  const [reportModalSession, setReportModalSession] = useState<DashboardSession | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedRow, setCopiedRow] = useState<number | null>(null);

  const handleCopy = async (id: string): Promise<void> => {
    await navigator.clipboard.writeText(id);
    setCopiedId(id);

    setTimeout(() => {
      setCopiedId(null);
      setCopiedRow(null);
    }, 1500);
  };

  const handleDownload = async (type: ReportType): Promise<void> => {
    if (reportModalSession == null) {
      return;
    }
    await onDownloadReport(
      reportModalSession.id,
      type,
      reportModalSession.type,
      reportModalSession.organizationId
    );
  };

  return (
    <div className="w-full overflow-hidden rounded-xl border border-border bg-card">
      <div className="p-4 border-b border-border/60 flex items-center justify-between">
        <h3 data-qa-id="db-table-title" className="font-semibold text-foreground">{t('dashboard.sessionsTable.title')}</h3>
        <div className="text-sm text-muted-foreground">
          {loading ? (
            <Skeleton className="h-4 w-20 bg-muted" />
          ) : (
            t('dashboard.sessionsTable.sessionCount', { count: totalCount })
          )}
        </div>
      </div>

      <div className="overflow-auto">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow className="border-b border-border/60 hover:bg-transparent">
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.sessionsTable.sdr')}</TableHead>
              {/* Gated like the other ids: only Tendril staff need the raw
                  ObjectId, and the column disappears entirely for everyone
                  else rather than sitting there empty. */}
              {isTendrilOrgUser === true && (
                <TableHead className="text-muted-foreground font-medium">{t('dashboard.sessionsTable.sessionId')}</TableHead>
              )}
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.sessionsTable.contactListName')}</TableHead>
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.sessionsTable.organization')}</TableHead>
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.sessionsTable.type')}</TableHead>
              <TableHead className="text-right text-muted-foreground font-medium">{t('dashboard.sessionsTable.contactsCount')}</TableHead>
              <TableHead className="text-right text-muted-foreground font-medium">{t('dashboard.sessionsTable.calls')}</TableHead>
              <TableHead className="text-right text-muted-foreground font-medium">{t('dashboard.sessionsTable.connectRate')}</TableHead>
              <TableHead className="text-right text-muted-foreground font-medium">{t('dashboard.sessionsTable.appointments')}</TableHead>
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.sessionsTable.date')}</TableHead>
              <TableHead className="text-muted-foreground font-medium">{t('dashboard.sessionsTable.status')}</TableHead>
              <TableHead className="text-center text-muted-foreground font-medium">{t('dashboard.sessionsTable.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableSkeleton rowCount={rowsPerPage} showSessionId={isTendrilOrgUser === true} />
            ) : sessions.length === 0 ? (
              <TableRow data-qa-id="session-row-0">
                <TableCell
                  data-qa-id="session-not-found"
                  colSpan={isTendrilOrgUser === true ? 12 : 11}
                  className="text-center py-12 text-muted-foreground"
                >
                  {t('dashboard.sessionsTable.noSessionsFound')}
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((session, r) => (
                <TableRow
                  data-qa-id={`session-row-${(session.id).slice(-6)}`}
                  key={session.id}
                  className="border-b border-border/60 hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => {
                    onSessionClick(session.id);
                  }}
                >
                  <TableCell className="py-4 flex flex-row">
                    <div className="flex items-center pr-2 gap-2">
                      <Avatar className="h-8 w-8 shadow">
                        {session.sdrProfilePicture != null && session.sdrProfilePicture !== '' && (
                          <AvatarImage
                            src={session.sdrProfilePicture}
                            alt={session.sdrName ?? 'User'}
                          />
                        )}
                        <AvatarFallback className="bg-primary/20 text-primary text-xs">
                          {getInitials(session.sdrName ?? '')}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div>
                      <div className="flex items-center">
                        <span className="text-foreground">
                          {session.sdrName ?? '-'}
                        </span>
                      </div>
                      {(isTendrilOrgUser === true) && (
                        <div>
                          <span className="text-xs">{session.sdrId}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleCopy(session.sdrId);
                              setCopiedRow(r);
                            }}
                            aria-label={t('dashboard.sessionsTable.copyUserId')}
                            title={t('dashboard.sessionsTable.copyUserId')}
                          >
                            {copiedRow === r && copiedId === session.sdrId
                              ? <Check className="size-3" />
                              : <Copy className="size-3" />
                            }
                          </Button>
                        </div>
                      )}
                    </div>
                  </TableCell>


                  {/* Its own column now. Sitting under the CSV badge, this id
                      read as if it belonged to the contact list — it is the
                      SESSION id, which is the one you need to look a session
                      up in the database or in a log. */}
                  {(isTendrilOrgUser === true) && (
                    <TableCell data-qa-id={`session-id-row-${r}`}>
                      <div className="flex items-center">
                        <span className="text-xs">{session.id}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleCopy(session.id);
                            setCopiedRow(r);
                          }}
                          aria-label={t('dashboard.sessionsTable.copySessionId')}
                          title={t('dashboard.sessionsTable.copySessionId')}
                        >
                          {copiedRow === r && copiedId === session.id
                            ? <Check className="size-3" />
                            : <Copy className="size-3" />
                          }
                        </Button>
                      </div>
                    </TableCell>
                  )}

                  <TableCell className="max-w-[200px]">
                    <div className="font-medium text-foreground break-words whitespace-normal">
                      {session.listNames !== '' ? session.listNames : t('dashboard.sessionsTable.untitledSession')}
                    </div>
                    {/* One line per list: a session can be built from several,
                        which is why `listNames` is a joined string. Joining the
                        ids the same way would give a copy button that yields
                        "id1 / id2" — useless for looking either one up. */}
                    {(isTendrilOrgUser === true) && session.contactListIds?.map((contactListId) => (
                      <div key={contactListId} className="flex items-center">
                        <span className="text-xs">{contactListId}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleCopy(contactListId);
                            setCopiedRow(r);
                          }}
                          aria-label={t('dashboard.sessionsTable.copyContactListId')}
                          title={t('dashboard.sessionsTable.copyContactListId')}
                        >
                          {copiedRow === r && copiedId === contactListId
                            ? <Check className="size-3" />
                            : <Copy className="size-3" />
                          }
                        </Button>
                      </div>
                    ))}
                  </TableCell>

                  <TableCell data-qa-id={`org-row-${r}`} className="text-foreground">
                    {session.organizationName ?? '-'}
                    {(isTendrilOrgUser === true) && (
                      <div>
                        <span className="text-xs">{session.organizationId}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleCopy(session.organizationId);
                            setCopiedRow(r);
                          }}
                          aria-label={t('dashboard.sessionsTable.copyOrgId')}
                          title={t('dashboard.sessionsTable.copyOrgId')}
                        >
                          {copiedRow === r && copiedId === session.organizationId
                            ? <Check className="size-3" />
                            : <Copy className="size-3" />
                          }
                        </Button>
                      </div>
                    )}
                  </TableCell>

                  <TableCell data-qa-id={`type-row-${r}`}>
                    <Badge
                      variant="outline"
                      className={`${TYPE_COLORS[session.type] ?? TYPE_COLORS.CSV} text-xs px-3 py-1 border font-medium`}
                    >
                      {TYPE_LABELS[session.type] ?? session.type}
                    </Badge>
                  </TableCell>

                  <TableCell data-qa-id={`contacts-row-${r}`} className="text-right tabular-nums text-foreground">
                    {session.totalContacts}
                  </TableCell>

                  <TableCell data-qa-id={`calls-row-${r}`} className="text-right tabular-nums text-foreground">
                    {session.totalCalls ?? 0}
                  </TableCell>

                  {/* Transfers over dials, the same ratio the session detail
                      shows, so the two cannot disagree for one session. */}
                  <TableCell data-qa-id={`connect-row-${r}`} className="text-right tabular-nums text-emerald-500">
                    {`${session.connectRate ?? 0}%`}
                  </TableCell>

                  <TableCell data-qa-id={`appointments-row-${r}`} className="text-right tabular-nums text-foreground">
                    {session.appointmentsSet ?? 0}
                  </TableCell>

                  <TableCell data-qa-id={`date-row-${r}`} className="text-muted-foreground">
                    {formatDate(session.startAtSession, i18n.language)}
                  </TableCell>

                  <TableCell data-qa-id={`status-row-${r}`}>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={`text-xs px-2.5 py-0.5 border ${STATUS_COLORS[session.status] ?? STATUS_COLORS[SessionStatus.FINISHED]}`}
                      >
                        {t(STATUS_LABELS[session.status] ?? 'dashboard.sessionsTable.statusFinished')}
                      </Badge>
                      {session.status === SessionStatus.STARTED && <StreamingPoint />}
                      {session.status === SessionStatus.PAUSED && <StreamingPoint paused />}
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center justify-center">
                      <Button
                        data-qa-id="export-session"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                        disabled={downloadingSessionId === session.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setReportModalSession(session);
                        }}
                        title={t('dashboard.sessionsTable.exportSession')}
                      >
                        {downloadingSessionId === session.id ? (
                          <IconLoader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <IconDownload className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationControls
        mode="server"
        totalItems={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[10, 20, 50]}
        isLoading={loading}
        className="border-t border-border/60"
      />

      <ReportDownloadModal
        open={reportModalSession !== null}
        onOpenChange={(open) => {
          if (!open) {
            setReportModalSession(null);
          } 
        }}
        listType={reportModalSession?.type ?? 'CSV'}
        onDownload={handleDownload}
      />
    </div>
  );
}
